import { useMemo } from 'react';
import { DoseId } from '~/domain/changes/ids';
import { domainTrace } from '~/domain/diagnostics/domainTrace';
import { createDoseDetailView } from '~/domain/projections/factories';
import { DoseDetailView } from '~/domain/projections/views';
import { useDomainVersion } from '~/hooks/domain/useDomainVersion';
import useSelectedPatient from '~/hooks/patient/useSelectedPatient';

/**
 * Returns the {@link DoseDetailView} for `doseId`, or `undefined` when no
 * dose is selected, no patient is loaded, or the id no longer resolves
 * (the dose was deleted while still selected).
 *
 * Replaces the `doseDataState` atom, which cached this same projection in
 * Recoil and had to be re-derived by hand from a `useEffect` on every dose
 * mutation. Deriving it from {@link useDomainVersion} means a tracked
 * write through `DoseService` invalidates it automatically.
 *
 * That covers the *tracked* fields only. The projection also carries the
 * dose's attached prescription metadata (`targets`), which is untracked and
 * therefore emits no changeset and triggers no invalidation here. It is
 * safe because `DoseMatrix.setTargets` is enforced set-once at ingest — a
 * dose's targets cannot change while this projection is live. Do not relax
 * that invariant without giving the write an invalidation path.
 *
 * Resolution goes through the patient repository rather than the session
 * so the projection is available in the window between a patient being
 * loaded and `PatientSessionProvider` creating its session — otherwise
 * the dose overlay would blank out for a frame on every patient load.
 * Without a session `useDomainVersion()` is a constant 0, which is
 * correct: with no tracker attached there are no tracked mutations to
 * invalidate on.
 */
export function useDoseDetailView(
    doseId: DoseId | null
): DoseDetailView | undefined {
    const patient = useSelectedPatient();
    const domainVersion = useDomainVersion();

    return useMemo(() => {
        if (!patient || !doseId) {
            return undefined;
        }
        const dose = patient.doses.find((d) => d.id === doseId);
        if (!dose) {
            // Selection outlived the entity (deleted dose). Report "no
            // dose" rather than throwing — a render must not crash on the
            // intermediate state before the selection atom is cleared.
            return undefined;
        }
        domainTrace.view('useDoseDetailView rebuild', {
            version: domainVersion,
        });
        return createDoseDetailView(dose);
        // domainVersion is intentionally a dependency the body only reads
        // for tracing: it is the invalidation trigger for in-place
        // aggregate mutations.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patient, doseId, domainVersion]);
}
